import { useCallback, useEffect, useRef, useState } from 'react';

const VIDEO_MIME_CANDIDATES = [
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
  'video/mp4',
];

function pickVideoMime(): string {
  for (const m of VIDEO_MIME_CANDIDATES) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m)) return m;
  }
  return '';
}

export type MediaUploaderState = 'idle' | 'connecting' | 'uploading' | 'flushing' | 'done' | 'error';

export type MediaUploader = {
  start: (stream: MediaStream, meetingId: string) => Promise<void>;
  stop: () => Promise<void>;
  bytesSent: number;
  state: MediaUploaderState;
};

export function useMediaUpload(): MediaUploader {
  const wsRef = useRef<WebSocket | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const queueRef = useRef<Blob[]>([]);
  const drainingRef = useRef(false);
  const wsReadyRef = useRef(false);
  const stateRef = useRef<MediaUploaderState>('idle');
  const [bytesSent, setBytesSent] = useState(0);
  const [state, setState] = useState<MediaUploaderState>('idle');

  const setS = (s: MediaUploaderState) => {
    stateRef.current = s;
    setState(s);
  };

  // Drain serially to preserve byte order in the WebM container.
  const drain = useCallback(async () => {
    if (drainingRef.current) return;
    drainingRef.current = true;
    try {
      while (queueRef.current.length > 0) {
        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN || !wsReadyRef.current) break;
        const blob = queueRef.current[0]!;
        let ab: ArrayBuffer;
        try {
          ab = await blob.arrayBuffer();
        } catch {
          queueRef.current.shift();
          continue;
        }
        try {
          ws.send(ab);
          setBytesSent((b) => b + ab.byteLength);
          queueRef.current.shift();
        } catch {
          break;
        }
      }
    } finally {
      drainingRef.current = false;
    }
  }, []);

  const cleanup = useCallback(() => {
    try { recorderRef.current?.state !== 'inactive' && recorderRef.current?.stop(); } catch {}
    recorderRef.current = null;
    try { wsRef.current?.close(); } catch {}
    wsRef.current = null;
    wsReadyRef.current = false;
    queueRef.current = [];
  }, []);

  const start = useCallback(
    async (stream: MediaStream, meetingId: string): Promise<void> => {
      if (stateRef.current === 'uploading' || stateRef.current === 'connecting') return;
      setS('connecting');
      setBytesSent(0);
      queueRef.current = [];
      wsReadyRef.current = false;

      const proto = location.protocol === 'https:' ? 'wss' : 'ws';
      const url = `${proto}://${location.host}/ws/upload?meeting_id=${encodeURIComponent(meetingId)}&kind=video`;
      const ws = new WebSocket(url);
      ws.binaryType = 'arraybuffer';
      wsRef.current = ws;

      const opened = new Promise<boolean>((resolve) => {
        const t = window.setTimeout(() => resolve(false), 5000);
        ws.addEventListener('open', () => { window.clearTimeout(t); resolve(true); }, { once: true });
        ws.addEventListener('error', () => { window.clearTimeout(t); resolve(false); }, { once: true });
        ws.addEventListener('close', () => { window.clearTimeout(t); resolve(false); }, { once: true });
      });

      ws.addEventListener('message', (e) => {
        try {
          const data = typeof e.data === 'string' ? e.data : '';
          if (data) {
            const msg = JSON.parse(data);
            if (msg?.type === 'ready') {
              wsReadyRef.current = true;
              setS('uploading');
              void drain();
            }
          }
        } catch {}
      });
      ws.addEventListener('close', () => {
        wsReadyRef.current = false;
        if (stateRef.current === 'uploading' || stateRef.current === 'flushing') {
          setS('done');
        }
      });
      ws.addEventListener('error', () => {
        if (stateRef.current !== 'done') setS('error');
      });

      const ok = await opened;
      if (!ok) {
        setS('error');
        cleanup();
        return;
      }

      const mime = pickVideoMime();
      const recorder = new MediaRecorder(
        stream,
        mime
          ? { mimeType: mime, videoBitsPerSecond: 1_500_000, audioBitsPerSecond: 96_000 }
          : undefined,
      );
      recorderRef.current = recorder;
      recorder.ondataavailable = (e) => {
        if (!e.data || e.data.size === 0) return;
        queueRef.current.push(e.data);
        void drain();
      };
      recorder.onerror = () => setS('error');
      recorder.start(2000);
    },
    [drain, cleanup],
  );

  const stop = useCallback(async () => {
    setS('flushing');
    const r = recorderRef.current;
    if (r && r.state !== 'inactive') {
      await new Promise<void>((resolve) => {
        const onStop = () => { r.removeEventListener('stop', onStop); resolve(); };
        r.addEventListener('stop', onStop, { once: true });
        try { r.stop(); } catch { resolve(); }
      });
    }
    recorderRef.current = null;

    // Final drain — wait until queue empty or we time out
    const startWait = Date.now();
    while (queueRef.current.length > 0 && Date.now() - startWait < 15000) {
      await drain();
      if (queueRef.current.length === 0) break;
      await new Promise((r) => setTimeout(r, 100));
    }

    try { wsRef.current?.close(); } catch {}
    wsRef.current = null;
    wsReadyRef.current = false;
    setS('done');
  }, [drain]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return { start, stop, bytesSent, state };
}
